<template>
  <div class="gallery-slideshow" @keydown="onKeydown" tabindex="0" ref="root">
    <div class="gallery-slideshow__container">
    <div class="gallery-slideshow__viewport" :class="{ 'gallery-slideshow__viewport--single': items.length <= 1 }">

      <!-- Previous slide (peek) -->
      <button
        v-if="items.length > 1"
        type="button"
        class="gallery-slideshow__slide gallery-slideshow__slide--prev gallery-slideshow__controller"
        :aria-label="'Previous image'"
        @click="prev"
      >
        <img
          v-if="prevItem.type === 'image'"
          :src="prevItem.large"
          alt=""
          class="gallery-slideshow__image"
        />
        <div v-else class="gallery-slideshow__video-thumb">
          <img
            :src="youtubePreview(prevItem.id)"
            alt=""
            class="gallery-slideshow__image"
          />
        </div>
        <span class="gallery-slideshow__nav gallery-slideshow__nav--prev" aria-hidden="true">
          <ChevronIcon />
        </span>
      </button>

      <!-- Current slide (center) -->
      <button
        ref="currentSlideRef"
        class="gallery-slideshow__slide gallery-slideshow__slide--current"
        @click="openModal"
        :aria-label="'Open image fullscreen'"
      >
        <img
          v-if="currentItem.type === 'image'"
          :src="currentItem.large"
          alt=""
          class="gallery-slideshow__image"
        />
        <div v-else class="gallery-slideshow__video-thumb">
          <img
            :src="youtubePreview(currentItem.id)"
            alt=""
            class="gallery-slideshow__image"
          />
          <span class="gallery-slideshow__play-icon">&#9654;</span>
        </div>
        <div
          v-if="currentItem.campaignHighlight"
          class="gallery-slideshow__campaign-highlight"
        >
          <p class="gallery-slideshow__campaign-highlight-text">
            {{ currentItem.campaignHighlight }}
          </p>
        </div>
        <figcaption v-if="currentItem.caption" class="wp-element-caption gallery-slideshow__caption">
          {{ currentItem.caption }}
        </figcaption>
      </button>

      <!-- Next slide (peek) -->
      <button
        v-if="items.length > 1"
        type="button"
        class="gallery-slideshow__slide gallery-slideshow__slide--next gallery-slideshow__controller"
        :aria-label="'Next image'"
        @click="next"
      >
        <img
          v-if="nextItem.type === 'image'"
          :src="nextItem.large"
          alt=""
          class="gallery-slideshow__image"
        />
        <div v-else class="gallery-slideshow__video-thumb">
          <img
            :src="youtubePreview(nextItem.id)"
            alt=""
            class="gallery-slideshow__image"
          />
        </div>
        <span class="gallery-slideshow__nav gallery-slideshow__nav--next" aria-hidden="true">
          <ChevronIcon />
        </span>
      </button>
    </div>

    <!-- Counter -->
    <div class="gallery-slideshow__counter">
      {{ currentIndex + 1 }} / {{ items.length }}
    </div>

    <!-- Fullscreen Modal -->
    <Teleport to="body">
      <Transition name="gallery-modal">
        <div
          v-if="modalOpen"
          class="gallery-slideshow-modal"
          tabindex="0"
          ref="modalRef"
        >
          <div class="gallery-slideshow-modal__content">
            <div class="gallery-slideshow-modal__media">
              <img
                v-if="currentItem.type === 'image'"
                :src="currentItem.large"
                alt=""
                class="gallery-slideshow-modal__image"
              />
              <div v-else class="gallery-slideshow-modal__video">
                <iframe
                  :src="youtubeEmbed(currentItem.id)"
                  title="Video"
                  allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                  allowfullscreen
                  class="gallery-slideshow-modal__iframe"
                ></iframe>
              </div>
              <div
                v-if="currentItem.campaignHighlight"
                class="gallery-slideshow__campaign-highlight"
              >
                <p class="gallery-slideshow__campaign-highlight-text">
                  {{ currentItem.campaignHighlight }}
                </p>
              </div>
            </div>

            <!-- Modal caption -->
            <p v-if="currentItem.caption" class="gallery-slideshow-modal__caption">
              {{ currentItem.caption }}
            </p>
          </div>

          <!-- Modal nav -->
          <template v-if="items.length > 1">
            <button class="gallery-slideshow-modal__nav gallery-slideshow-modal__nav--prev" @click="prev" aria-label="Previous">
              <ChevronIcon />
            </button>
            <button class="gallery-slideshow-modal__nav gallery-slideshow-modal__nav--next" @click="next" aria-label="Next">
              <ChevronIcon />
            </button>
          </template>

          <!-- Close button -->
          <button class="gallery-slideshow-modal__close" @click="closeModal" aria-label="Close">
            <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>

          <!-- Modal counter -->
          <div class="gallery-slideshow-modal__counter">
            {{ currentIndex + 1 }} / {{ items.length }}
          </div>
        </div> <!-- ends gallery-slideshow-modal__content -->
      </Transition>
    </Teleport>
  </div> <!-- ends gallery-slideshow__container -->
</div> <!-- ends gallery-slideshow -->
</template>

<script setup>
import {
  ref,
  computed,
  nextTick,
  onMounted,
  onBeforeUnmount,
} from 'vue';
import ChevronIcon from './icons/ChevronIcon.vue';

const props = defineProps({
  items: {
    type: Array,
    required: true,
  },
});

const root = ref(null);
const modalRef = ref(null);
const currentSlideRef = ref(null);
const currentIndex = ref(0);
const modalOpen = ref(false);

// Touch handling
const touchStartX = ref(0);
const touchEndX = ref(0);

const wrap = (index) => ((index % props.items.length) + props.items.length) % props.items.length;

const currentItem = computed(() => props.items[currentIndex.value]);
const prevItem = computed(() => props.items[wrap(currentIndex.value - 1)]);
const nextItem = computed(() => props.items[wrap(currentIndex.value + 1)]);

function youtubePreview(id) {
  return `https://img.youtube.com/vi/${id}/hqdefault.jpg`;
}

function youtubeEmbed(id) {
  return `https://www.youtube.com/embed/${id}?autoplay=1`;
}

function next() {
  currentIndex.value = wrap(currentIndex.value + 1);
}

function prev() {
  currentIndex.value = wrap(currentIndex.value - 1);
}

function openModal() {
  modalOpen.value = true;
  document.documentElement.style.overflowY = 'hidden';
  // On the document, so the keys keep working after a click on the backdrop
  // has moved focus out of the modal.
  document.addEventListener('keydown', onModalKeydown);
  nextTick(() => {
    modalRef.value?.focus();
  });
}

function closeModal() {
  modalOpen.value = false;
  document.documentElement.style.overflowY = '';
  document.removeEventListener('keydown', onModalKeydown);
  nextTick(() => {
    currentSlideRef.value?.focus();
  });
}

function onKeydown(e) {
  if (e.key === 'ArrowLeft') {
    e.preventDefault();
    prev();
  } else if (e.key === 'ArrowRight') {
    e.preventDefault();
    next();
  } else if (e.key === 'Enter' || e.key === ' ') {
    e.preventDefault();
    const target = e.target;
    if (target?.closest('.gallery-slideshow__slide--prev')) {
      prev();
    } else if (target?.closest('.gallery-slideshow__slide--next')) {
      next();
    } else {
      openModal();
    }
  }
}

function onModalKeydown(e) {
  if (e.key === 'Escape') {
    closeModal();
  } else if (e.key === 'ArrowLeft') {
    e.preventDefault();
    prev();
  } else if (e.key === 'ArrowRight') {
    e.preventDefault();
    next();
  }
}

function onTouchStart(e) {
  touchStartX.value = e.changedTouches[0].screenX;
}

function onTouchEnd(e) {
  touchEndX.value = e.changedTouches[0].screenX;
  const diff = touchStartX.value - touchEndX.value;
  const SWIPE_THRESHOLD = 50;
  if (diff > SWIPE_THRESHOLD) {
    next();
  } else if (diff < -SWIPE_THRESHOLD) {
    prev();
  }
}

onMounted(() => {
  const el = root.value;
  if (el) {
    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchend', onTouchEnd, { passive: true });
  }
});

onBeforeUnmount(() => {
  const el = root.value;
  if (el) {
    el.removeEventListener('touchstart', onTouchStart);
    el.removeEventListener('touchend', onTouchEnd);
  }
  if (modalOpen.value) {
    document.documentElement.style.overflowY = '';
  }
  document.removeEventListener('keydown', onModalKeydown);
});
</script>

<style lang="scss">
@import "~styles/definitions";

// ── Slideshow Carousel ──

.gallery-slideshow {
  width: 100%;
  outline: none;
}

// Grid so row height = center content; then prev/next at 80% of that row
.gallery-slideshow__viewport {
  position: relative;
  display: grid;
  grid-template-columns: 12% 70% 12%;
  align-items: center;
  justify-items: center;
  justify-content: center;  // add this
  overflow: hidden;
  padding: 1.5rem 0;
  gap: 0 0.75rem;

  @include media-breakpoint-up(md) {
    grid-template-columns: 10% 65% 10%;
    gap: 0 1rem;
  }

  // Slides fill their grid cell
  > .gallery-slideshow__slide {
    width: 100%;
    max-width: 65rem;
  }

  // Single image: one column, current slide centered
  &.gallery-slideshow__viewport--single {
    grid-template-columns: 1fr;
  }
}

.gallery-slideshow__slide {
  position: relative;
  flex-shrink: 0;
  border: none;
  padding: 0;
  background: none;
  cursor: pointer;
  overflow: hidden;
  border-radius: 0;
  box-shadow: 0 10px 20px rgba(0, 0, 0, .5);
  transition: transform 0.3s ease, opacity 0.3s ease;

  &:focus-visible {
    outline: 3px solid $color-green-2025;
    outline-offset: 3px;
  }
}

.gallery-slideshow__slide--prev,
.gallery-slideshow__slide--next {
  height: 80%;
  min-height: 0;
  opacity: 0.85;
  align-self: center;
}

.gallery-slideshow__slide--current {
  width: 70%;
  max-height: 45rem;
  z-index: 2;
  display: flex;
  flex-flow: column;
  justify-content: center;
  align-items: center;

  .gallery-slideshow__image {
    width: auto;
    height: auto;
    max-width: 100%;
    max-height: 45rem;
    object-fit: contain;
  }

  .gallery-slideshow__video-thumb {
    aspect-ratio: 16 / 9;
    max-height: 45rem;
  }

  .gallery-slideshow__video-thumb .gallery-slideshow__image {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  @include media-breakpoint-up(md) {
    width: 65%;
  }
}

.gallery-slideshow__image {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.gallery-slideshow__video-thumb {
  position: relative;
  width: 100%;
  height: 100%;
}

.gallery-slideshow__play-icon {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 3rem;
  color: $color-white;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.5);
  pointer-events: none;
}

.gallery-slideshow__campaign-highlight {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
  box-sizing: border-box;
  width: 19.25rem; // ~309px
  height: 19.25rem;
  margin: 1.25rem;
  padding: 1.5rem;
  background-color: $color-peach-light;
  border-top-right-radius: 100%;
  display: flex;
  align-items: flex-end;
  pointer-events: none;
  transform: rotate(180deg);

  // Match apartment-campaign-promo highlight box below xxl
  @include media-breakpoint-down(xxl) {
    width: 15rem;
    height: 15rem;
    padding: 1.25rem; // ~1.5 * 15/19.25
  }

  @include media-breakpoint-down(xl) {
    width: 13rem;
    height: 13rem;
    padding: 1rem; // ~1.5 * 13/19.25
  }

  @include media-breakpoint-down(lg) {
    width: 10rem;
    height: 10rem;
    padding: 0.75rem; // ~1.5 * 10/19.25
  }

  @include media-breakpoint-down(sm) {
    width: 6rem;
    height: 6rem;
    padding: 0.5rem; // ~1.5 * 6/19.25
  }
}

.gallery-slideshow__campaign-highlight-text {
  margin: 0;
  color: $color-green-2025;
  font-family: $ff-avenir;
  font-size: r(32);
  font-weight: $font-weight-bold;
  line-height: 1.25;
  text-align: right;
  transform: rotate(180deg);

  @include media-breakpoint-down(xxl) {
    font-size: 1.75rem;
  }

  @include media-breakpoint-down(xl) {
    font-size: 1.5rem;
  }

  @include media-breakpoint-down(lg) {
    font-size: 1rem;
  }

  @include media-breakpoint-down(sm) {
    font-size: 0.75rem;
  }
}

// ── Nav Buttons ──

$nav-size-mobile: 2.5rem;
$nav-size-desktop: 3rem;

.gallery-slideshow__nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 5;

  display: flex;
  align-items: center;
  justify-content: center;

  width: $nav-size-mobile;
  height: $nav-size-mobile;
  border-radius: 50%;
  border: 2px solid $color-white;
  background-color: $color-white;
  color: $color-green-2025;
  cursor: pointer;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.12);
  transition: background-color 0.2s, color 0.2s;

  @include media-breakpoint-up(md) {
    width: $nav-size-desktop;
    height: $nav-size-desktop;
  }

  &:hover,
  &:focus-visible {
    background-color: $color-green-2025;
    color: $color-white;
    border-color: $color-green-2025;
  }
}

/** -----------
 *  Controllers
 *. ----------- */

.gallery-slideshow__controller {
  position: relative;
  overflow: visible; // so nav is never clipped

  .gallery-slideshow__image {
    position: absolute;
    top: 0;
    left: 0;
  }

  // Center nav on the controller's image
  .gallery-slideshow__nav {
    left: 50%;
    right: auto;
    top: 50%;
    transform: translate(-50%, -50%);
  }

  // Nav looks hovered when parent controller is hovered/focused
  &:hover .gallery-slideshow__nav,
  &:focus-visible .gallery-slideshow__nav {
    background-color: $color-green-2025;
    color: $color-white;
    border-color: $color-green-2025;
  }
}

.gallery-slideshow__nav--prev {
  left: 1rem;

  @include media-breakpoint-up(md) {
    left: 2rem;
  }

  .icon > svg {
    transform: scaleX(-1);
  }
}

.gallery-slideshow__controller .gallery-slideshow__nav--prev {
  left: 50%;
  transform: translate(-50%, -50%);

  .icon > svg {
    transform: scaleX(-1);
  }
}

.gallery-slideshow__nav--next {
  right: 1rem;

  @include media-breakpoint-up(md) {
    right: 2rem;
  }
}

.gallery-slideshow__controller .gallery-slideshow__nav--next {
  right: auto;
  left: 50%;
  transform: translate(-50%, -50%);
}

// ── Counter ──

$counter-font-size: $font-size-base;
$counter-font-weight: $font-weight-bold;

.gallery-slideshow__counter {
  text-align: center;
  font-family: $ff-inter;
  font-size: $counter-font-size;
  font-weight: $counter-font-weight;
  color: $color-green-2025;
  margin-top: .5 * $theme-spacing;
}

.gallery-slideshow__caption {
  width: 100%;
  text-align: center;
  padding: $theme-spacing;
}

// ── Fullscreen Modal ──

.gallery-slideshow-modal {
  position: fixed;
  inset: 0;
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: $color-modal-backdrop;
}

.gallery-slideshow-modal__content {
  max-width: 90vw;
  max-height: 85vh;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-flow: column;
}

.gallery-slideshow-modal__media {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  max-width: 90vw;
  max-height: 85vh;
}

.gallery-slideshow-modal__image {
  max-width: 90vw;
  max-height: 85vh;
  object-fit: contain;
  border-radius: 0.5rem;
}

.gallery-slideshow-modal__video {
  position: relative;
  width: 80vw;
  max-width: 960px;
  aspect-ratio: 16 / 9;
}

.gallery-slideshow-modal__iframe {
  width: 100%;
  height: 100%;
  border: none;
  border-radius: 0.5rem;
}

// Modal nav – same style as basic nav (white bg, green arrows)
.gallery-slideshow-modal__nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 10;

  display: flex;
  align-items: center;
  justify-content: center;

  width: $nav-size-mobile;
  height: $nav-size-mobile;
  border-radius: 50%;
  border: 2px solid $color-white;
  background-color: $color-white;
  color: $color-green-2025;
  cursor: pointer;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.12);
  transition: background-color 0.2s, color 0.2s, border-color 0.2s;

  @include media-breakpoint-up(md) {
    width: $nav-size-desktop;
    height: $nav-size-desktop;
  }

  &:hover,
  &:focus-visible {
    background-color: $color-green-2025;
    color: $color-white;
    border-color: $color-green-2025;
  }
}

.gallery-slideshow-modal__nav--prev {
  left: 1rem;

  @include media-breakpoint-up(md) {
    left: 2rem;
  }

  .icon > svg {
    transform: scaleX(-1);
  }
}

.gallery-slideshow-modal__nav--next {
  right: 1rem;

  @include media-breakpoint-up(md) {
    right: 2rem;
  }
}

.gallery-slideshow-modal__close {
  @include modal-close-button;
  z-index: 10;
}

.gallery-slideshow-modal__caption {
  position: static;
  width: 100%;
  text-align: center;
  font-family: $ff-inter;
  font-size: $font-size-base;
  color: rgba(255, 255, 255, 0.9);
  padding: $theme-spacing;
  border-radius: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.gallery-slideshow-modal__counter {
  position: absolute;
  bottom: 1.5rem;
  left: 50%;
  transform: translateX(-50%);
  font-family: $ff-inter;
  font-size: $counter-font-size;
  font-weight: $counter-font-weight;
  color: rgba(255, 255, 255, 0.8);
}

// ── SEO fallback images — hidden visually, visible to crawlers ──
// These are the static <img> tags rendered by PHP inside the Vue mount container.
// Vue replaces them on mount; hiding them prevents a flash of unstyled content.
[data-vue-component="GallerySlideshow"] > img,
[data-vue-component="GallerySlideshow"] > figure {
  display: none;
}

// ── Modal Transitions ──

.gallery-modal-enter-active,
.gallery-modal-leave-active {
  transition: opacity 0.25s ease;
}

.gallery-modal-enter-from,
.gallery-modal-leave-to {
  opacity: 0;
}
</style>
